/**
 * Open Food Facts categories (data/off-categories.json, H17): a category tag as Open Food Facts
 * writes it ("en:sliced-breads", "de:passierte-tomaten", "fr:pains-de-mie") → the product of the
 * catalogue it names. For a scanned product the app does not know: its categories say what it is
 * more reliably than its name, which is often French or English ("100% mie Complet").
 *
 * Only food: no drinks (KellerLog's), and no tag too general to name one product
 * ("en:plant-based-foods", "en:snacks").
 */
import data from "../data/off-categories.json" with { type: "json" };
import { product } from "./catalog.ts";
import type { Product } from "./catalog.ts";

const CATEGORIES: Readonly<Record<string, string>> = data.categories;

/** The taxonomy the mapping was made from, and when. */
export const OFF_CATEGORIES_SOURCE: { taxonomy: string; read: string; counts: string } = data.source;

/**
 * A tag as the file keys it: lower case, without accents ("de:brötchen" and "de:brotchen" are one
 * tag; Open Food Facts keeps German umlauts, drops French accents).
 */
export const categoryKey = (tag: string): string => tag.trim().toLowerCase().replace(/ß/g, "ss").normalize("NFD").replace(/\p{M}/gu, "");

/** The product one category tag names, or undefined when the tag is not mapped. */
export function productForCategory(tag: string): Product | undefined {
  const key = categoryKey(tag);
  return Object.hasOwn(CATEGORIES, key) ? product(CATEGORIES[key]) : undefined;
}

/**
 * The product a list of category tags names: the most specific mapped tag. The tags come as Open
 * Food Facts orders them (categories_tags), the most general first, so the last mapped one wins:
 * ["en:breads", "en:sliced-breads", "fr:pains-de-mie"] gives Toast, ["en:breads"] alone Brot.
 */
export function productForCategories(tags: readonly string[]): Product | undefined {
  for (let i = tags.length - 1; i >= 0; i--) {
    const hit = productForCategory(tags[i]!);
    if (hit) return hit;
  }
  return undefined;
}

/** The mapped tags. */
export const mappedCategories = (): string[] => Object.keys(CATEGORIES);
